import React, { useState, useEffect, useRef } from 'react';
import {
  startSession,
  endSession,
  getSessionHistory,
  getGoals
} from '../../services/api';

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TimerIcon from '@mui/icons-material/Timer';
import FlagIcon from '@mui/icons-material/Flag';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';

const SessionControls = ({ 
  token, 
  conversationId,
  onSessionUpdate, // Callback when session status changes
  sessionEnabled, // Whether sessions are enabled for this conversation
  activeSession, // Current active session object if any
  clientSettings // Client therapy settings
}) => {
  // State
  const [goals, setGoals] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generateSummary, setGenerateSummary] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [timeProgressPercent, setTimeProgressPercent] = useState(0);
  
  // Timer interval ref
  const timerRef = useRef(null);

  // Load goals and session history on mount or when conversationId changes
  useEffect(() => {
    if (token && conversationId) {
      setLoading(true); // Set loading true when we start fetching history
      setError('');
      loadGoals();
      loadSessionHistory().finally(() => setLoading(false)); // Clear loading after history is fetched
    } else {
      // If no conversationId, clear history and don't show loading/error for it
      setSessionHistory([]);
      setLoading(false);
      setError('');
    }
  }, [token, conversationId]);

  // Timer effect
  useEffect(() => {
    if (activeSession && !activeSession.endTime) {
      // Calculate elapsed time from session start
      const startTime = new Date(activeSession.startTime).getTime();
      const duration = (activeSession.duration || 45) * 60 * 1000; // Convert minutes to ms
      
      // Initial calculation
      calculateElapsedTime(startTime, duration);
      
      // Set up timer
      timerRef.current = setInterval(() => {
        calculateElapsedTime(startTime, duration);
      }, 1000);
    } else {
      // Clear timer when session ends
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeSession]);

  const calculateElapsedTime = (startTime, duration) => {
    const now = Date.now();
    const elapsed = Math.floor((now - startTime) / 1000); // in seconds
    const durationSecs = Math.floor(duration / 1000);
    
    setElapsedTime(elapsed);
    setSessionDuration(durationSecs);
    setTimeProgressPercent(Math.min((elapsed / durationSecs) * 100, 100));
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours > 0 ? `${hours}:` : ''}${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Load client's goals
  const loadGoals = async () => {
    try {
      const fetchedGoals = await getGoals(token);
      setGoals(fetchedGoals.filter(goal => goal.status === 'active'));
    } catch (err) {
      console.error('Error loading goals:', err);
      setError('Failed to load goals');
    }
  };
  
  // Load session history for this conversation
  const loadSessionHistory = async () => {
    if (!conversationId) {
      setSessionHistory([]); // Clear history if no conversationId
      return;
    }
    // setLoading(true); // Moved to useEffect to cover initial load better
    // setError(''); // Moved to useEffect
    try {
      const history = await getSessionHistory(conversationId, token);
      // Sort sessions by start time (newest first)
      const sortedSessions = [...history].sort((a, b) => {
        return new Date(b.startTime) - new Date(a.startTime);
      });
      setSessionHistory(sortedSessions);
    } catch (err) {
      console.error('Error loading session history:', err);
      setError(err.message || 'Failed to load session history');
      setSessionHistory([]); // Clear history on error
    } 
    // finally { // Moved to useEffect
    //   setLoading(false);
    // }
  };

  // Handle goal selection
  const handleGoalToggle = (goalId) => {
    setSelectedGoals(prev => {
      if (prev.includes(goalId)) {
        return prev.filter(id => id !== goalId);
      } else {
        return [...prev, goalId];
      }
    });
  };

  // Start a new session
  const handleStartSession = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await startSession(conversationId, selectedGoals, token);
      
      // Update parent components
      if (onSessionUpdate) {
        onSessionUpdate({
          sessionActive: true,
          activeSession: result.session
        });
      }
      
      // Reset selected goals and close dialog
      setSelectedGoals([]);
      setStartDialogOpen(false);
      
      // Refresh session history
      loadSessionHistory();
    } catch (err) {
      console.error('Error starting session:', err);
      setError(err.message || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  // End the current session
  const handleEndSession = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await endSession(conversationId, generateSummary, token);
      
      // Update parent components
      if (onSessionUpdate) {
        onSessionUpdate({
          sessionActive: false,
          activeSession: null,
          nextSession: result.nextSession
        });
      }
      
      // Close dialog
      setEndDialogOpen(false);
      
      // Refresh session history
      loadSessionHistory();
    } catch (err) {
      console.error('Error ending session:', err);
      setError(err.message || 'Failed to end session');
    } finally {
      setLoading(false);
    }
  };

  // Get session mode from client settings
  const getSessionMode = () => {
    if (!clientSettings || !clientSettings.sessionMode) {
      return 'continuous';
    }
    return clientSettings.sessionMode;
  };

  // Get session duration from active session or client settings
  const getSessionDuration = () => {
    if (activeSession && activeSession.duration) {
      return activeSession.duration;
    }
    
    if (clientSettings && clientSettings.sessionLength) {
      return clientSettings.sessionLength;
    }
    
    return 45; // Default 45 minutes
  };

  // Determine if sessions are available based on client settings
  const isSessionsAvailable = () => {
    const mode = getSessionMode();
    return mode === 'structured' || mode === 'hybrid';
  };

  if (!isSessionsAvailable()) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Structured sessions are not enabled for this client. Their therapy mode is set to "continuous".
      </Alert>
    );
  }

  // Render the session controls
  return (
    <>
      <Paper 
        elevation={0} 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 2, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Session Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
              <TimerIcon sx={{ mr: 1 }} />
              Session Status
            </Box>
          </Typography>
          
          {activeSession ? (
            <Chip 
              label="Session Active" 
              color="primary" 
              sx={{ fontWeight: 'bold' }}
            />
          ) : (
            <Chip 
              label="No Active Session" 
              variant="outlined" 
              color="default"
            />
          )}
        </Box>
        
        <Divider sx={{ my: 1.5 }} />
        
        {/* Session Details or Controls */}
        {loading && !activeSession ? ( // Show loader only if no active session and history is loading
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>Loading session status...</Typography>
          </Box>
        ) : activeSession ? (
          // Active Session Details (existing logic)
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Started:</strong> {new Date(activeSession.startTime).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Duration:</strong> {activeSession.duration} minutes
                </Typography>
                {activeSession.goals && activeSession.goals.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Session Goals:</strong></Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {activeSession.goals.map(goalId => {
                        const goal = goals.find(g => g._id === goalId);
                        return (<Chip key={goalId} label={goal ? goal.description : 'Unknown Goal'} size="small" variant="outlined"/>);
                      })}
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>{formatTime(elapsedTime)}</Typography>
                    <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>/ {formatTime(sessionDuration)}</Typography>
                  </Box>
                  <Box sx={{ width: '100%', mb: 1 }}>
                    <LinearProgress variant="determinate" value={timeProgressPercent} color={timeProgressPercent > 95 ? "error" : "primary"} sx={{ height: 8, borderRadius: 4 }}/>
                  </Box>
                  {timeProgressPercent >= 100 && (<Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>Time limit reached</Typography>)}
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button variant="contained" color="secondary" onClick={() => setEndDialogOpen(true)} startIcon={<StopIcon />}> End Session </Button>
            </Box>
          </Box>
        ) : (
          // No Active Session - Conditional Start Button UI
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
            {sessionHistory.length === 0 ? (
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                Ready to begin? Start your first therapy session to structure your conversation and track progress.
              </Typography>
            ) : (
              <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
                Your previous session is complete. Start a new therapy session to continue.
              </Typography>
            )}
            <Button variant="contained" color="primary" onClick={() => setStartDialogOpen(true)} startIcon={<PlayArrowIcon />} size="large">
              {sessionHistory.length === 0 ? 'Start First Session' : 'Start New Session'}
            </Button>
          </Box>
        )}
        
        {/* Show next scheduled session if applicable (logic unchanged) */}
        {!activeSession && clientSettings && clientSettings.nextScheduledSession && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="primary">
              Next session scheduled: {new Date(clientSettings.nextScheduledSession).toLocaleString()}
            </Typography>
          </Box>
        )}
        
        {/* Session History Summary */}
        {sessionHistory.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {sessionHistory.length} previous {sessionHistory.length === 1 ? 'session' : 'sessions'} completed
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* Start Session Dialog */}
      <Dialog open={startDialogOpen} onClose={() => setStartDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start New Therapy Session</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You're about to start a {getSessionDuration()} minute therapy session.
          </Typography>
          
          {goals.length > 0 ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Select goals to focus on during this session:
              </Typography>
              <FormGroup>
                {goals.map(goal => (
                  <FormControlLabel
                    key={goal._id}
                    control={
                      <Checkbox 
                        checked={selectedGoals.includes(goal._id)}
                        onChange={() => handleGoalToggle(goal._id)}
                      />
                    }
                    label={goal.description}
                  />
                ))}
              </FormGroup>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" paragraph>
              No active goals found. You can still start a session without specific goals.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleStartSession} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Start Session'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* End Session Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>End Current Session</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You're about to end your current therapy session.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={generateSummary}
                onChange={(e) => setGenerateSummary(e.target.checked)}
                color="primary"
              />
            }
            label="Generate session summary and reflection prompts"
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Session duration: {formatTime(elapsedTime)} / {formatTime(sessionDuration)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleEndSession} 
            variant="contained" 
            color="secondary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'End Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionControls;
